import { ThemeEvents } from '@theme/events';

const BUSY_ATTR = 'variant-busy';
const REF_SELECTOR = '[data-variant-loader]';
const ROOT_SELECTORS = ['[data-variant-root]', '.product-main', '.product__info', '.shopify-section', 'dialog', 'product-card'];
const FALLBACK_TIMEOUT = 12000;

class VariantBusyManager {
  activeScopes = new WeakMap();
  activeSources = new Set();

  constructor() {
    document.addEventListener(ThemeEvents.variantSelected, this.handleSelect, true);
    document.addEventListener(ThemeEvents.variantUpdate, this.handleUpdate);
  }

  handleSelect = (event) => {
    const source = this.resolveSource(event);
    if (!source) return;

    this.applyBusyState(source);
  };

  handleUpdate = (event) => {
    const source = this.resolveSource(event);
    if (!source) return;

    this.clearBusyState(source);
  };

  resolveSource(event) {
    const source = event.target;
    if (!(source instanceof Element)) return undefined;
    return source;
  }

  applyBusyState(source) {
    this.clearBusyState(source);

    const scopeRoot = this.resolveScopeRoot(source);
    if (!scopeRoot) return;

    const nodes = this.collectScopedNodes(scopeRoot);
    nodes.forEach(this.flagNodeBusy);

    const timeoutId = window.setTimeout(() => {
      this.clearBusyState(source);
    }, FALLBACK_TIMEOUT);

    this.activeScopes.set(source, { nodes, timeoutId });
    this.activeSources.add(source);
    this.updateGlobalBusyState();
  }

  clearBusyState(source) {
    const entry = this.activeScopes.get(source);
    if (!entry) return;

    const { nodes, timeoutId } = entry;
    window.clearTimeout(timeoutId);

    nodes.forEach(this.unflagNodeBusy);

    this.activeScopes.delete(source);
    this.activeSources.delete(source);
    this.updateGlobalBusyState();
  }

  resolveScopeRoot(source) {
    for (const selector of ROOT_SELECTORS) {
      const match = source.closest(selector);
      if (match) return match;
    }
    return document.body;
  }

  collectScopedNodes(scopeRoot) {
    const nodes = new Set();
    if (scopeRoot.matches(REF_SELECTOR)) {
      nodes.add(scopeRoot);
    }
    scopeRoot.querySelectorAll(REF_SELECTOR).forEach((node) => nodes.add(node));
    nodes.add(scopeRoot);
    return nodes;
  }

  flagNodeBusy = (node) => {
    if (!(node instanceof Element)) return;
    node.setAttribute(`data-${BUSY_ATTR}`, 'true');
    if (node instanceof HTMLElement || node instanceof SVGElement) {
      node.setAttribute('aria-busy', 'true');
    }
  };

  unflagNodeBusy = (node) => {
    if (!(node instanceof Element)) return;
    node.removeAttribute(`data-${BUSY_ATTR}`);
    if ((node instanceof HTMLElement || node instanceof SVGElement) && node.getAttribute('aria-busy') === 'true') {
      node.removeAttribute('aria-busy');
    }
  };

  updateGlobalBusyState() {
    if (this.activeSources.size) {
      document.documentElement.dataset.variantBusy = 'true';
    } else {
      delete document.documentElement.dataset.variantBusy;
    }
  }
}

new VariantBusyManager();
